import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState, type ReactNode } from 'react';
import {
  Alert,
  Badge,
  Button,
  Fact,
  Facts,
  Field,
  KanbanTag,
  MultiSelect,
  Select,
  SideDrawer,
  TextArea,
  linkTextClasses,
} from '@evogard/ui';
import { format as formatMoney, formatDate, formatInstant, fromColumn } from '@evogard/domain';
import type { DeskBoardCard, DeskCan, DeskSummary } from '@evogard/contract';
import { api, ok } from '../../../lib/api.js';
import { cardPlace, destinationOf, type BoardCard } from './deskBoard.js';
import { useDeskMove, type DeskMove } from './DeskMoveDialog.js';
import { DeskLabelsModal, deskLabelsKey } from './DeskLabelsModal.js';
import { DESK_KEY, useInvalidateDesk } from './useDesk.js';

/**
 * A lateral do card: os dados, o histórico e os atos que não cabem num clique.
 *
 * O que cada pessoa vê aqui é o que ela pode fazer NAQUELA base (`can`, do
 * resumo da base do card) e naquele card — dono, ou quem distribui. O
 * servidor confere de novo; esconder o botão é só para não oferecer o que vai
 * ser recusado.
 */
export function CardDrawer({
  tenant,
  id,
  summary,
  onClose,
}: {
  tenant: { id: number; slug: string; name: string };
  id: number;
  /** O resumo da base do card; ausente quando a pessoa não vê a Mesa dela. */
  summary: DeskSummary | undefined;
  onClose: () => void;
}) {
  const can = summary?.can;
  const me = summary?.me ?? '';
  const detail = useQuery({
    queryKey: [...DESK_KEY, 'card', tenant.slug, id],
    queryFn: async () =>
      ok(
        await api.billingDesk.detail({ params: { tenant: tenant.slug, id } }),
        200,
        'Não foi possível abrir o card.',
      ),
  });

  const card = detail.data?.card;
  return (
    <SideDrawer
      eyebrow={`Mesa de cobrança · ${tenant.slug.toUpperCase()}`}
      title={card?.name ?? 'Card'}
      onClose={onClose}
      closeLabel="Fechar o card"
    >
      {detail.error && <Alert tone="erro">{detail.error.message}</Alert>}
      {card && can && summary && (
        <div className="flex flex-col gap-6">
          <CardFacts card={card} />
          <Acts card={{ ...card, tenant }} summary={summary} onShown={onClose} />
          <Edit card={card} tenant={tenant.slug} me={me} can={can} />
          <Labels card={card} tenant={tenant.slug} me={me} can={can} />
          <Section title="Histórico">
            <ol className="flex flex-col gap-2">
              {detail.data!.history.map((e) => (
                <li key={e.id} className="text-sm">
                  <span className="flex flex-wrap items-center gap-2">
                    <span className="font-medium text-texto">{e.title}</span>
                    {e.source === 'kanban' && <Badge tone="alerta">Kanban</Badge>}
                  </span>
                  {(e.from || e.to) && (
                    <span className="block text-texto">
                      {e.from ?? '—'} → {e.to ?? '—'}
                    </span>
                  )}
                  {e.details && <span className="block text-xs text-texto-suave">{e.details}</span>}
                  <span className="block text-xs text-texto-suave">
                    {formatInstant(e.at)}
                    {e.actor ? ` · ${e.actor}` : ` · Sistema — ${e.via ?? 'rotina'}`}
                  </span>
                </li>
              ))}
              {detail.data!.history.length === 0 && (
                <li className="text-sm text-texto-suave">Nenhum registro ainda.</li>
              )}
            </ol>
          </Section>
        </div>
      )}
    </SideDrawer>
  );
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className="flex flex-col gap-3">
      <h3 className="text-sm font-semibold text-texto">{title}</h3>
      {children}
    </section>
  );
}

function CardFacts({ card }: { card: DeskBoardCard }) {
  const amount = card.amount === null ? null : fromColumn(card.amount);
  return (
    <Facts>
      <Fact label="Onde está">{cardPlace(card)}</Fact>
      <Fact label="Atendente">{card.operator?.name ?? 'Ninguém — está na fila mãe'}</Fact>
      <Fact label="Telefone">{card.phone ?? '—'}</Fact>
      <Fact label="Placa">{card.plates.join(', ') || '—'}</Fact>
      <Fact label="Valor">{amount === null ? '—' : formatMoney(amount)}</Fact>
      <Fact label="Vencimento">{card.dueDate ? formatDate(card.dueDate) : '—'}</Fact>
      <Fact label="Consultor">{card.consultant ?? '—'}</Fact>
      <Fact label="Documento">{card.document ?? '—'}</Fact>
      <Fact label="Boleto">
        {card.boleto
          ? `${card.boleto.code}${card.boleto.nossoNumero ? ` · nosso nº ${card.boleto.nossoNumero}` : ''}${card.boleto.status ? ` · ${card.boleto.status}` : ''}`
          : card.vehicle
            ? 'Sem boleto da casa — vencimento e valor foram digitados'
            : 'Card manual, sem boleto da casa'}
      </Fact>
      {!card.boleto && (
        <Fact label="Cadastro">
          {card.vehicle
            ? `Veículo ${card.vehicle.code ?? ''} do cadastro — nome, documento e consultor vêm de lá${card.vehicle.missing ? ' (o veículo deixou de vir do ERP)' : ''}`
            : 'Placa fora do cadastro — nome e documento são os digitados'}
        </Fact>
      )}
      {card.erpPaidOn && <Fact label="Pago no ERP">{formatDate(card.erpPaidOn)}</Fact>}
      {card.conversationRef && <Fact label="Protocolo">{card.conversationRef}</Fact>}
      <Fact label="Na Mesa desde">{formatInstant(card.createdAt)}</Fact>
    </Facts>
  );
}

/**
 * Os atos sobre o card — o que no legado eram os botões e os selects do card.
 * Cada movimento abre o `DeskMoveDialog`, que diz para onde o card vai antes
 * do sim e mostra onde ele foi parar depois.
 */
function Acts({ card, summary, onShown }: { card: BoardCard; summary: DeskSummary; onShown: () => void }) {
  const start = useDeskMove();
  const { can, me } = summary;
  const [target, setTarget] = useState('');
  const params = { tenant: card.tenant.slug, id: card.id };
  const open = card.closedAt === null;
  const mine = card.operator?.id === me;
  const owns = mine || can.distribute;
  const act = (move: DeskMove) => start({ card, move, summary, onShown });

  const sameFlow = summary.operators.filter(
    (o) => o.listed && o.userId !== card.operator?.id && (card.flow === 'receptiva' ? o.receptiveFlow : o.activeFlow),
  );
  const chosen = sameFlow.find((o) => o.userId === target);
  const proofUrl = useMutation({
    mutationFn: async () =>
      ok(await api.billingDesk.proofUrl({ params }), 200, 'Não foi possível abrir o comprovante.'),
  });

  return (
    <Section title={open ? 'O que fazer' : 'Card fechado'}>
      {/* passar para alguém é só de quem distribui — ver `BillingDeskService.handOver` */}
      {open && can.assign && can.distribute && (
        <div className="flex flex-wrap items-end gap-2">
          <div className="min-w-56 flex-1">
            <Select label="Passar para" value={target} onChange={(e) => setTarget(e.target.value)}>
              <option value="">Escolha…</option>
              {sameFlow.map((o) => (
                <option key={o.userId} value={o.userId}>
                  {destinationOf(card, o)}
                </option>
              ))}
            </Select>
          </div>
          <Button
            variant="contorno"
            disabled={!chosen}
            onClick={() => {
              if (!chosen) return;
              setTarget('');
              act({ kind: 'pass', operator: chosen });
            }}
          >
            Passar
          </Button>
          {can.distribute && card.flow === 'ativa' && card.operator !== null && (
            <Button variant="discreto" onClick={() => act({ kind: 'queue' })}>
              Devolver à fila mãe
            </Button>
          )}
        </div>
      )}

      {open && can.handle && owns && (
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => act({ kind: 'collectionPaid' })}>Pago cobrança</Button>
          <Button variant="contorno" onClick={() => act({ kind: 'autoPaid' })}>
            Pago automático
          </Button>
        </div>
      )}

      {open && can.handle && (owns || card.createdBy === me) && (
        <div>
          <Button variant="perigo" size="sm" onClick={() => act({ kind: 'discard' })}>
            Tirar do quadro
          </Button>
        </div>
      )}

      {!open && can.credit && (card.outcome === 'pago_automatico' || card.outcome === 'pago_cobranca') && (
        <div>
          <Button onClick={() => act({ kind: 'credit' })}>
            {card.outcome === 'pago_automatico' ? 'Passar para pago cobrança' : 'Trocar o crédito'}
          </Button>
        </div>
      )}

      {!open && can.reopen && (
        <div>
          <Button variant="contorno" onClick={() => act({ kind: 'reopen' })}>
            Devolver ao quadro
          </Button>
        </div>
      )}

      {card.hasProof && (
        <p className="text-sm">
          {proofUrl.data ? (
            <a href={proofUrl.data.url} target="_blank" rel="noreferrer" className={linkTextClasses('font-medium')}>
              Abrir o comprovante
            </a>
          ) : (
            <Button size="sm" variant="discreto" loading={proofUrl.isPending} onClick={() => proofUrl.mutate()}>
              Ver o comprovante
            </Button>
          )}
        </p>
      )}
      {proofUrl.error && <Alert tone="erro">{proofUrl.error.message}</Alert>}
    </Section>
  );
}

/** Observação, telefone e protocolo — só o dono ou quem distribui. */
function Edit({ card, tenant, me, can }: { card: DeskBoardCard; tenant: string; me: string; can: DeskCan }) {
  const invalidate = useInvalidateDesk();
  const [note, setNote] = useState(card.note ?? '');
  const [phone, setPhone] = useState(card.phone ?? '');
  const [protocol, setProtocol] = useState(card.conversationRef ?? '');
  useEffect(() => {
    setNote(card.note ?? '');
    setPhone(card.phone ?? '');
    setProtocol(card.conversationRef ?? '');
  }, [card.note, card.phone, card.conversationRef]);

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingDesk.edit({
          params: { tenant, id: card.id },
          body: {
            note: note.trim() || null,
            phone: phone.trim() || null,
            conversationRef: protocol.trim() || null,
          },
        }),
        200,
        'Não foi possível guardar.',
      ),
    onSuccess: () => invalidate(),
  });

  if (!can.handle || (card.operator?.id !== me && !can.distribute)) return null;
  return (
    <Section title="Anotações">
      <form
        className="flex flex-col gap-3"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        <TextArea label="Observação" value={note} onChange={(e) => setNote(e.target.value)} maxLength={4000} />
        <Field label="Telefone de contato" value={phone} onChange={(e) => setPhone(e.target.value)} />
        <Field label="Protocolo da conversa" value={protocol} onChange={(e) => setProtocol(e.target.value)} />
        {save.error && <Alert tone="erro">{save.error.message}</Alert>}
        <div>
          <Button type="submit" variant="secundario" loading={save.isPending}>
            Guardar
          </Button>
        </div>
      </form>
    </Section>
  );
}

/**
 * As etiquetas do card, das etiquetas da Mesa da base (27/10/2026).
 *
 * Pôr e tirar é do dono do card, ou de quem distribui: nos cards dos outros o
 * operador só vê (decisão do dono, 27/10/2026). Criar etiqueta é de qualquer
 * operador, pelo "Gerenciar etiquetas", como no Kanban.
 */
function Labels({ card, tenant, me, can }: { card: DeskBoardCard; tenant: string; me: string; can: DeskCan }) {
  const invalidate = useInvalidateDesk();
  const options = useQuery({
    queryKey: deskLabelsKey(tenant),
    queryFn: async () =>
      ok(await api.billingDesk.labelOptions({ params: { tenant } }), 200, 'Não foi possível carregar as etiquetas.'),
  });
  const [selected, setSelected] = useState<string[]>(card.labels.map((l) => String(l.id)));
  useEffect(() => setSelected(card.labels.map((l) => String(l.id))), [card.labels]);
  const [managing, setManaging] = useState(false);

  const save = useMutation({
    mutationFn: async () =>
      ok(
        await api.billingDesk.setLabels({
          params: { tenant, id: card.id },
          body: { labelIds: selected.map(Number) },
        }),
        200,
        'Não foi possível trocar as etiquetas.',
      ),
    onSuccess: () => invalidate(),
  });

  const canTag = can.handle && (card.operator?.id === me || can.distribute);
  const items = options.data?.items ?? [];
  /* a API de uma versão antes responde `configured: false` quando a base não tinha setor */
  const ready = options.data?.configured === true;

  return (
    <Section title="Etiquetas">
      {card.labels.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {card.labels.map((l) => (
            <KanbanTag key={l.id} color={l.color}>
              {l.name}
            </KanbanTag>
          ))}
        </div>
      )}
      {ready && canTag && items.length > 0 && (
        <>
          <MultiSelect
            label="Etiquetas do card"
            options={items.map((l) => ({ value: String(l.id), label: l.name }))}
            selected={selected}
            onChange={setSelected}
            placeholder="Nenhuma"
          />
          {save.error && <Alert tone="erro">{save.error.message}</Alert>}
          <div>
            <Button size="sm" variant="secundario" loading={save.isPending} onClick={() => save.mutate()}>
              Guardar etiquetas
            </Button>
          </div>
        </>
      )}
      {ready && can.handle && items.length === 0 && (
        <p className="text-sm text-texto-suave">A Mesa desta base ainda não tem etiquetas. Crie a primeira.</p>
      )}
      {!canTag && card.labels.length === 0 && <p className="text-sm text-texto-suave">Nenhuma.</p>}
      {ready && can.handle && (
        <div>
          <Button size="sm" variant="discreto" icon="etiqueta" onClick={() => setManaging(true)}>
            Gerenciar etiquetas
          </Button>
        </div>
      )}
      {managing && <DeskLabelsModal tenant={tenant} onClose={() => setManaging(false)} />}
    </Section>
  );
}
